import { defineStore } from 'pinia'
import {
  removeSession,
  setStore,
  getStore,
  setAccessToken,
  setRefreshToken,
  setTenantId,
  getAccessToken,
  getRefreshToken,
  getTenantId
} from '../utils/store.js'
import { loginByUsername, signOut, getUserInfo, loginBySocial } from '@/api/login'
import MenuApi from '../api/menu.js'
import TenantApi from '../api/admin/tenant.js'
import DeptApi from '../api/admin/dept.js'
import UserApi from '../api/admin/user.js'
import SaleApi from '../api/invoicing/sale'
import OrderApi from '../api/invoicing/order-form'
import DailyReportApi from '../api/synergy/daily-report'
import employeeLeaveApi from '../api/synergy/employee-leave'
import checkInRecordDetailApi from '../api/synergy/check-in-record-detail'
import TodoApi from '../api/synergy/todo'
import website from '@/const/website'
import { encryptUser, deepClone } from '@/utils'
import { isURL, validatenull } from '@/utils/validate'
import routerUtils from '../router/dynamic-routers.js'
import { useTagStore } from './tag.js'
import { menuPathConfigs } from '../const/menu-path-configs.js'

const menuApi = new MenuApi()
const tenantApi = new TenantApi()
const deptApi = new DeptApi()
const userApi = new UserApi()
const saleApi = new SaleApi()
const orderApi = new OrderApi()
const dailyReportApi = new DailyReportApi()
const todoApi = new TodoApi()

function addPath(ele, first) {
  const menu = website.menu
  const propsConfig = menu.props
  const propsDefault = {
    label: propsConfig.label || 'name',
    path: propsConfig.path || 'path',
    icon: propsConfig.icon || 'icon',
    children: propsConfig.children || 'children'
  }
  const icon = ele[propsDefault.icon]
  ele[propsDefault.icon] = validatenull(icon) ? menu.iconDefault : icon
  const hasChildren = ele[propsDefault.children] && ele[propsDefault.children].length !== 0
  if (!hasChildren) ele[propsDefault.children] = []
  if (!hasChildren && first && !isURL(ele[propsDefault.path])) {
    ele[propsDefault.path] = ele[propsDefault.path] + '/index'
  } else {
    ele[propsDefault.children].forEach(child => {
      addPath(child)
    })
  }
}

const configs = {
  // 销售单审批
  [menuPathConfigs.sale]: {
    api: () => saleApi.getApprovePage({ size: 0 }),
    perm: 'invoicing_sale_view'
  },
  // 订单表管理
  [menuPathConfigs.order]: {
    api: () => orderApi.getApprovePage({ size: 0 }),
    perm: 'order_orderform_view'
  },
  // 工作日志审批
  [menuPathConfigs.dailyReport]: {
    api: () => dailyReportApi.getList({ size: 0, approval: true, status: '审批中' }),
    perm: 'ss_daily_report'
  },
  // 请休假审批
  [menuPathConfigs.employeeLeave]: {
    api: () => employeeLeaveApi.getList({ size: 0, approvalStatus: '待审批' }),
    perm: 'leave_approval'
  },
  // 员工打卡审批
  [menuPathConfigs.checkInRecordDetail]: {
    api: () => checkInRecordDetailApi.getList({ size: 0, checkInStatus: '待审批' }),
    perm: 'check_in_record_approval'
  },
  // 待处理的待办事项（无权限检查）
  [menuPathConfigs.todo]: {
    api: () => todoApi.getList({ size: 0, status: ['0'] })
  }
}

export const useUserStore = defineStore('user', {
  state: () => ({
    access_token: getAccessToken() || '',
    refresh_token: getRefreshToken() || '',
    tenantId: getTenantId(),
    lastTenantId: getStore({ name: 'lastTenantId' }),
    userInfo: {},
    tenantInfo: {},
    userInfoLoaded: false,
    permissions: {},
    roles: [],
    menus: [],
    topMenus: [],
    loadingUserInfo: false,
    userAll: [],
    deptAll: [],
    menuCounts: {},
    pathToIdMap: {}
  }),
  actions: {
    // 登出
    async logout() {
      this.access_token = ''
      this.refresh_token = ''
      this.tenantId = ''
      this.userInfo = {}
      this.roles = []
      this.permissions = {}
      this.menus = []
      this.menuCounts = {}
      this.pathToIdMap = {}
      this.tenantInfo = {}
      this.userAll = []
      this.deptAll = []
      this.userInfoLoaded = false
      removeSession('access_token')
      removeSession('refresh_token')
      removeSession('tenant_id')
      removeSession('local_deployment_tenant')
      useTagStore().delAllTags()
      return await signOut()
    },
    setLastTenantId(tenantId) {
      this.lastTenantId = tenantId
      setStore({ name: 'lastTenantId', content: tenantId })
    },
    setCurrentTenantId(tenantId) {
      this.tenantId = tenantId
      setTenantId(tenantId)
    },
    // 路径标准化处理
    normalizePath(path) {
      return path
        .replace(/\/+/g, '/') // 去除多余斜杠
        .replace(/\/$/, '') // 去除结尾斜杠
        .toLowerCase()
    },
    // 初始化路径映射
    buildPathToIdMap() {
      const map = {}
      const traverse = menus => {
        menus.forEach(menu => {
          // 排除父级菜单（parentId=-1）
          if (menu.parentId !== '-1' && menu.path) {
            const normalizedPath = this.normalizePath(menu.path)
            map[normalizedPath] = menu.id
          }
          if (menu.children) traverse(menu.children)
        })
      }
      traverse(this.menus)
      this.pathToIdMap = map
    },
    // 通过路径更新特定菜单数量，提供给业务页面调用
    async updateMenuCountByPath(targetPath) {
      const normalizedPath = this.normalizePath(targetPath)
      const menuId = this.pathToIdMap[normalizedPath]

      if (!menuId) {
        console.warn(`路径 ${targetPath} 未找到对应菜单项`)
        return
      }

      try {
        const result = await this.safeAsync(configs[targetPath].api, menuId)
        this.menuCounts[menuId] = result?.data?.total || 0
      } catch (e) {
        console.error(`[${menuId}]数量更新失败:`, e)
        // 保留最后一次成功值
      }
    },
    // 统一错误处理函数
    async safeAsync(asyncFunc, menuId) {
      try {
        return await asyncFunc()
      } catch (e) {
        console.error('接口调用失败:', e)
        return { data: { total: this.menuCounts[menuId] || 0 } }
      }
    },
    // 处理菜单数量获取
    async handleMenuCount(menuFinder, asyncFunc) {
      const promises = []

      const traverse = menuList => {
        menuList.forEach(menu => {
          // 从二级菜单开始匹配
          if (menu.parentId !== '-1' && menuFinder(menu)) {
            promises.push(
              this.safeAsync(asyncFunc)
                .then(result => {
                  this.menuCounts[menu.id] = result.data?.total ?? 0
                })
                .catch(() => {
                  // 保留最后一次成功值
                  console.warn(`[${menu.name}]数量更新失败，保持原值`)
                })
            )
          }
          if (menu.children) traverse(menu.children)
        })
      }

      traverse(this.menus)
      await Promise.allSettled(promises)
    },
    // 创建带权限检查的任务
    createSafeTask(menuFinder, apiFunc, permission) {
      if ((permission && this.permissions[permission]) || !permission) {
        this.handleMenuCount(menuFinder, apiFunc)
      }
    },
    // 获取菜单数量
    async fetchMenuCounts() {
      const tasks = Object.entries(configs).map(([path, config]) => {
        return {
          check: i => this.normalizePath(i.path) === path,
          api: config.api,
          ...(config.perm && { perm: config.perm })
        }
      })
      for (const task of tasks) {
        try {
          this.createSafeTask(task.check, task.api, task.perm)
        } catch (e) {
          console.error('单项检查失败:', e)
        }
      }
    },
    // 获取系统左侧菜单
    async getMenus() {
      const result = (await menuApi.getMenu()).data
      const menus = deepClone(result)
      if (this.roles?.length) {
        const findAdminRoles = this.roles.find(x => x.roleCode === 'GENERAL_MANAGER')
        if (findAdminRoles) {
          menus.push({
            parentId: '-1',
            weight: 888,
            name: '升级续费',
            path: '/my-package/index',
            keepAlive: '0',
            sortOrder: 888,
            icon: 'icon-peoplelist',
            menuType: '0',
            permission: null,
            label: '升级续费',
            children: [],
            meta: {}
          })
        }
      }
      menus.push({
        parentId: '-1',
        weight: 999,
        name: '邀请好友注册',
        path: '/invite/index',
        keepAlive: '0',
        sortOrder: 999,
        icon: 'icon-peoplelist',
        menuType: '0',
        permission: null,
        label: '邀请好友注册',
        children: [],
        meta: {}
      })
      menus.forEach(ele => {
        addPath(ele)
      })
      routerUtils.initUserRouters(menus)
      this.menus = menus
      // 初始化路径映射
      this.buildPathToIdMap()
      // 在获取菜单后调用数量获取
      this.fetchMenuCounts()
    },
    // 获取顶部菜单
    async getTopMenus() {
      this.topMenus = (await menuApi.getTopMenu()).data
    },
    async getUserInfoWithPermissions() {
      const result = (await getUserInfo()).data
      this.userInfo = result.userVO
      this.roles = result.userVO.roleList || []
      // convert to permission object
      const permissionObj = {}
      result.permissions.forEach(x => (permissionObj[x] = true))
      this.permissions = permissionObj
    },
    // 获取当前登录用户所在租户的信息
    async loadUserTenantInfo() {
      const result = await tenantApi.getTenantInfo()
      this.tenantInfo = result || {}
    },
    // 获取当前登录用户的信息
    async loadUserAllInfo() {
      if (this.userInfoLoaded) return
      this.loadingUserInfo = true
      try {
        await Promise.all([
          this.getUserInfoWithPermissions(),
          this.getMenus(),
          this.getTopMenus(),
          this.loadUserTenantInfo(),
          this.loadDeptAndUsers()
        ])
        this.userInfoLoaded = true
      } catch (e) {
        console.error('Failed to load user info: ' + e.message)
      } finally {
        this.loadingUserInfo = false
      }
    },
    async getUserList() {
      const result = await userApi.getUsersWithProfiles()
      return result.data
    },
    async loadDeptAndUsers(force = false) {
      // Check if data is already loaded
      const isDataLoaded = this.userAll?.length && this.deptAll?.length

      if (!isDataLoaded || force) {
        try {
          // Fetch both departments and users in parallel
          const [deptResponse, userResponse] = await Promise.all([
            deptApi.getAll(),
            userApi.getAll({ status: 'normal' })
          ])

          // Store the results
          this.deptAll = deptResponse.data
          this.userAll = userResponse.data
        } catch (error) {
          console.error('Failed to load departments and users:', error.message)
          return { depts: [], users: [] }
        }
      }

      // Return the data (either freshly loaded or from cache)
      return {
        depts: this.deptAll,
        users: this.userAll
      }
    },
    // 根据账号密码登录
    async login(userInfo) {
      const user = website.passwordEnc ? encryptUser(userInfo) : userInfo
      const response = await loginByUsername(
        user.username,
        user.password,
        user.code,
        user.randomStr
      )
      this.setTokens(response)
    },
    // 根据第三方社交帐号登录，如微信扫码登录
    async loginBySocial(params) {
      const response = await loginBySocial(params.state, params.code, params.tenantId)
      this.setTokens(response)
    },
    setTokens(response) {
      setAccessToken(response.access_token)
      setRefreshToken(response.refresh_token)
      this.access_token = response.access_token
      this.refresh_token = response.refresh_token
    }
  }
})
